import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from './Toast';
import { type ToasterToast, useToast } from './use-toast';
import { useCallback, useEffect, useRef } from 'react';

export function Toaster() {
  const { toasts, dismiss } = useToast();

  const viewportRef = useRef<HTMLOListElement | null>(null);

  const stackToasts = useCallback(() => {
    const heights: number[] = [];
    const toastElements = getToastElementsFromToastsState(toasts);

    toastElements.forEach(([, toast], index) => {
      if (!toast) return;
      const height = toast.clientHeight;
      heights.push(height);
      toast.setAttribute('data-front', String(index === 0));
      toast.setAttribute('data-hidden', String(index > 2));
      toast.style.setProperty('--index', String(index));
      toast.style.setProperty('--height', `${height}px`);
      toast.style.setProperty('--z-index', String(40 - index));
      const hoverOffsetY = heights
        .slice(0, index)
        .reduce((res, next) => (res += next), 0);
      toast.style.setProperty('--hover-offset-y', `-${hoverOffsetY}px`);
    });
  }, [toasts]);

  useEffect(() => {
    stackToasts();
    const viewport = viewportRef.current;
    const toastElements = getToastElementsFromToastsState(toasts);
    if (viewport) {
      let timeout: ReturnType<typeof setTimeout>;
      const handleFocus = () => {
        clearTimeout(timeout);
        toastElements.forEach(([, toast]) => {
          if (toast) {
            toast.setAttribute('data-hover', String(true));
          }
        });
        stackToasts();
      };

      const handleBlur = (event: FocusEvent) => {
        if (
          !viewport.contains(event.target as Node | null) ||
          viewport === event.target
        ) {
          timeout = setTimeout(() => {
            toastElements.forEach(([, toast]) => {
              if (toast) {
                toast.removeAttribute('data-hover');
              }
            });
            stackToasts();
          }, 100);
        }
      };

      viewport.addEventListener('pointermove', handleFocus);
      viewport.addEventListener('pointerleave', handleBlur);
      viewport.addEventListener('focusin', handleFocus);
      viewport.addEventListener('focusout', handleBlur);
      return () => {
        viewport.removeEventListener('pointermove', handleFocus);
        viewport.removeEventListener('pointerleave', handleBlur);
        viewport.removeEventListener('focusin', handleFocus);
        viewport.removeEventListener('focusout', handleBlur);
      };
    }
  }, [stackToasts, toasts]);

  return (
    <ToastProvider>
      {toasts.map(({ id, title, description, action, open, ...props }, i) => (
        <Toast
          key={id}
          id={`toast-${id}`}
          open={open}
          {...props}
          className="toast translate-y-[var(--y)] absolute scale-[var(--scale)] bottom-4 right-4 z-[var(--z-index)]"
          onOpenChange={() => {
            dismiss(id);
            stackToasts();
          }}
        >
          <div className="grid gap-1">
            {title && <ToastTitle>{title}</ToastTitle>}
            {description && <ToastDescription>{description}</ToastDescription>}
          </div>
          {action}
          <ToastClose />
        </Toast>
      ))}
      <ToastViewport
        className="[--stack-gap:0.5rem] h-fit empty:p-0 p-6"
        ref={viewportRef}
      />
    </ToastProvider>
  );
}

function getToastElementsFromToastsState(toasts: ToasterToast[]) {
  return toasts
    .filter((toast) => toast.open)
    .map((toast) => {
      return [toast.id, document.getElementById(`toast-${toast.id}`)] as const;
    });
}
